import React, { useState, useEffect } from 'react';

/**
 * Dev-only indicator of which git branch / worktree the dev server is serving.
 *
 * The branch name is read at RUNTIME from `public/dev-branch.txt`, which `preview.sh`
 * rewrites on every swap (and `yarn dev` seeds at launch). Because the dev server
 * serves that file statically, polling it lets the badge update live when you run
 * `yarn preview` — without restarting `yarn dev`. This is why we can't use a
 * build-time env var like REACT_APP_GIT_BRANCH: CRA inlines those once at boot, so
 * they'd stay frozen (always "local") no matter how many times you swap branches.
 * REACT_APP_GIT_BRANCH is still honored as a fallback for the initial paint.
 *
 * Renders nothing in production builds (NODE_ENV !== 'development'), so it can never
 * appear on tools.skywayclassics.com. A feature branch shows in amber to stand out;
 * master / local shows in a neutral green. Click × to hide it for the session.
 */
export default function DevBranchBadge() {
  const [hidden, setHidden] = useState(false);
  const [branch, setBranch] = useState(process.env.REACT_APP_GIT_BRANCH || 'local');

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return undefined;

    let cancelled = false;
    const poll = async () => {
      try {
        const res = await fetch('/dev-branch.txt', { cache: 'no-store' });
        if (!res.ok) return;
        const text = (await res.text()).trim();
        // The dev server's history fallback serves index.html for unknown paths;
        // ignore that so a missing file doesn't show HTML as the "branch".
        if (!cancelled && text && !text.startsWith('<')) setBranch(text);
      } catch (_) {
        /* dev server not reachable — keep last known value */
      }
    };

    poll();
    const id = setInterval(poll, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);

  if (process.env.NODE_ENV !== 'development' || hidden) return null;

  const isFeature = branch !== 'local' && branch !== 'master';
  const accent = isFeature ? '#ed6c02' : '#2e7d32'; // amber for feature branch, green for master/local

  return (
    <div
      style={{
        position: 'fixed',
        left: 8,
        bottom: 8,
        zIndex: 2147483647,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 8px',
        borderRadius: 6,
        border: `1px solid ${accent}`,
        backgroundColor: 'rgba(0,0,0,0.82)',
        color: '#fff',
        font: '11px/1.2 ui-monospace, SFMono-Regular, Menlo, monospace',
        boxShadow: '0 1px 4px rgba(0,0,0,0.4)',
        pointerEvents: 'auto',
        userSelect: 'text',
      }}
      title="Git branch the local dev server is serving (dev only)"
    >
      <span style={{ color: accent, fontWeight: 700 }}>⎇</span>
      <span>{branch}</span>
      <span
        role="button"
        aria-label="Hide branch badge"
        onClick={() => setHidden(true)}
        style={{ cursor: 'pointer', opacity: 0.6, paddingLeft: 2 }}
      >
        ×
      </span>
    </div>
  );
}
